import { Camera } from '@babylonjs/core/Cameras/camera';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import { Quaternion, Vector2, Vector3 } from '@babylonjs/core/Maths';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { WebXRCamera } from '@babylonjs/core/XR/webXRCamera';
import { WebXRSessionManager } from '@babylonjs/core/XR/webXRSessionManager';
import { WebXRRenderTarget } from '@babylonjs/core/XR/webXRTypes';
import { Scene } from '@babylonjs/core/scene';
import { eBackground } from '@kb3d/models/classes/enums';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { Subject } from 'rxjs';
import { XrHitTester } from './xr-hit-tester';
import { XrLight } from './xr-light';
import { XrMarker } from './xr-marker';
import { XrShadowPlane } from './xr-shadow-plane';
import { IXrTransientHitResult, XrTransientHitTester } from './xr-transient-hit-tester';

/**
 * Stub for future lighting estimation types
 */
declare global {
    interface XRLightEstimate {
        readonly sphericalHarmonicsCoefficients: Float32Array;
        readonly primaryLightDirection: DOMPointReadOnly;
        readonly primaryLightIntensity: DOMPointReadOnly;
    }
    interface XRLightProbe {
        readonly probeSpace: XRSpace;
    }
    interface XRSession {
        requestLightProbe(): Promise<XRLightProbe>;
    }
    interface XRFrame {
        getLightEstimate(lightProbe: XRLightProbe): XRLightEstimate;
    }
}
// interface XRSystem extends EventTarget {
//     requestSession(mode: XRSessionMode, options?: XRSessionInit): Promise<XRSession>;
//     isSessionSupported(mode: XRSessionMode): Promise<boolean>;
// }

// declare global {
//     interface Navigator {
//         xr?: XRSystem;
//     }
// }

enum eXrState {
    searchingForHit,
    initialPlacement,
    placed,
}

const ROTATION_RATE = 1.5;
const SCALE_SNAP = 1.2;

export class Xr {
    constructor(public v: KbViewer, public scene: Scene) {}

    public onSessionEnd = new Subject<void>();
    private _sessionMgr: WebXRSessionManager;
    private _session: XRSession;
    private _renderTarget: WebXRRenderTarget;
    private _state = eXrState.searchingForHit;
    private _originalSceneCamera: Camera | null;
    private _originalBackground: eBackground;
    private _xrCamera: WebXRCamera;
    private _refSpace: XRReferenceSpace;
    private _initialHitTester: XrHitTester | undefined;
    private _transientHitTester: XrTransientHitTester | undefined;
    private _marker: XrMarker;
    private _light: XrLight;
    private _shadow: XrShadowPlane;
    private _rootNode: TransformNode;
    private _viewerPose: XRViewerPose | undefined;
    private _lastFrame: XRFrame | undefined;
    private _isTranslating: boolean;
    private _isRotating: boolean;
    private _isScaling: boolean;
    private _goalScale = 0;
    private _goalPosition: Vector3;
    private _goalYaw = 0;
    private _lastScalar = 0;
    private _dragNodeStartPos: Vector3 | undefined;
    private _selectStartScreenPos: Vector2 | undefined;
    private _fingerStartPos: Vector3 | undefined;
    private _dragHitTester: XrTransientHitTester | undefined;
    private _scaleSnapHigh: number = SCALE_SNAP;
    private _scaleSnapLow: number = 1 / SCALE_SNAP;
    private _naturalScale = 1;
    private _lightProbe: XRLightProbe | undefined;
    private _wasJustPlaced = false;
    private sessionEndingPromise?: Promise<void>;

    public startSession(mode: XRSessionMode, refSpaceType: XRReferenceSpaceType, overlayElem?: HTMLElement) {
        const xrInitOptions = {
            optionalFeatures: ['light-estimation'],
            requiredFeatures: ['hit-test'],
        } as XRSessionInit;

        if (overlayElem) {
            overlayElem.addEventListener('beforexrselect', ev => {
                if (ev.target != overlayElem) {
                    ev.preventDefault();
                }
            });
            xrInitOptions.optionalFeatures?.push('dom-overlay');
            (xrInitOptions as any).domOverlay = {
                root: overlayElem,
            };
        }
        if (refSpaceType !== 'viewer' && refSpaceType !== 'local') {
            xrInitOptions.optionalFeatures?.push(refSpaceType);
        }

        this._naturalScale = 1 / this.v.sceneNode.unitsPerMeter;
        this._sessionMgr = new WebXRSessionManager(this.scene);
        this._sessionMgr.onXRSessionEnded.addOnce(() => this.sessionEnded());

        this._xrCamera = new WebXRCamera('', this.scene, this._sessionMgr);

        return this._sessionMgr
            .initializeAsync()
            .then(() => {
                this._renderTarget = this._sessionMgr.getWebXRRenderTarget();
                return this._sessionMgr.initializeSessionAsync(mode, xrInitOptions).then(() => {
                    this._session = this._sessionMgr.session;
                });
            })
            .then(() => {
                return this._sessionMgr.setReferenceSpaceTypeAsync(refSpaceType).then(refSpace => {
                    this._refSpace = refSpace;
                });
            })
            .then(() => {
                return this._renderTarget.initializeXRLayerAsync(this._sessionMgr.session);
            })
            .then(() => {
                return this._sessionMgr.updateRenderState({
                    depthFar: this._xrCamera!.maxZ,
                    depthNear: this._xrCamera!.minZ,
                    baseLayer: this._renderTarget.xrLayer!,
                });
            })
            .then(() => {
                this._rootNode = this.v.rendererManager.sceneRenderer.get3d(this.v.sceneNode)!; //get the root transform node for our scene... this is what we will move to position the scene in AR
                this._rootNode.setEnabled(false);

                //subscribe to render loop
                this._sessionMgr.onXRFrameObservable.add((frame: XRFrame) => this.onXRFrame(frame));
                this._sessionMgr.runXRRenderLoop(); //start the render loop

                this._originalSceneCamera = this.scene.activeCamera;
                this.scene.activeCamera = this._xrCamera!; //this line is important
                this.scene.autoClear = false;
                this._xrCamera!.compensateOnFirstFrame = false;

                this._originalBackground = this.v.sceneNode.background;
                this.v.sceneNode.background = eBackground.transparent;

                //add xr input handlers
                this._session.addEventListener('selectstart', this.onSelectStart);
                this._session.addEventListener('selectend', this.onSelectEnd);

                this._initialHitTester = new XrHitTester(this._sessionMgr);
                return this._initialHitTester.initialize();
            });
    }

    public endSession() {
        if (this._sessionMgr) {
            this.sessionEndingPromise = this._sessionMgr.exitXRAsync();
            return this.sessionEndingPromise;
        } else {
            return Promise.resolve();
        }
    }

    protected sessionEnded() {
        if (this.sessionEndingPromise) {
            this.sessionEndingPromise.then(() => {
                this.sessionEndingPromise = undefined;
                this.sessionEnded();
            });
        } else {
            this._session.removeEventListener('selectstart', this.onSelectStart);
            this._session.removeEventListener('selectend', this.onSelectEnd);

            if (this._xrCamera) this._xrCamera.dispose();
            if (this._sessionMgr) this._sessionMgr.dispose();
            if (this._renderTarget) this._renderTarget.dispose();
            if (this._initialHitTester) this._initialHitTester.dispose();
            if (this._marker) this._marker.dispose();
            if (this._light) this._light.dispose();
            if (this._shadow) this._shadow.dispose();

            if (this._originalSceneCamera) {
                this.scene.activeCamera = this._originalSceneCamera;
            }
            this.scene.autoClear = true;
            this.v.sceneNode.background = this._originalBackground || eBackground.transparent;
            this._rootNode.scaling = Vector3.One();
            this._rootNode.rotation = Vector3.Zero();
            this._rootNode.position = Vector3.Zero();
            this._rootNode.setEnabled(true);
            this.onSessionEnd.next();
        }
    }

    protected onXRFrame(frame: XRFrame) {
        this._lastFrame = frame;
        //console.log(`user height: ${this._xrCamera.realWorldHeight}`);
        this._viewerPose = frame.getViewerPose(this._sessionMgr.referenceSpace);

        this.placeInitialMarker(frame);

        this.processInput(frame);
        this.setSceneTransform();
        this.runLightingEstimation(frame);
    }

    private _lightingEstimationCount = 0;
    protected runLightingEstimation(frame: XRFrame) {
        if (this._lightProbe) {
            this._lightingEstimationCount++;
            if (this._lightingEstimationCount == 30) {
                this._lightingEstimationCount = 0;
                const estimate = frame.getLightEstimate(this._lightProbe);
                // const probePose = frame.getPose(this._lightProbe.probeSpace, this._refSpace);
                // console.log({probePose});
                // console.log({ lightEstimate: estimate });
                if (estimate && this._light) {
                    const direction = new Vector3(
                        estimate.primaryLightDirection.x,
                        estimate.primaryLightDirection.y,
                        estimate.primaryLightDirection.z
                    );
                    direction.negateInPlace();
                    this._light.setDirection(direction.x, direction.y, direction.z);

                    const intensity = Math.max(
                        1.0,
                        Math.max(
                            estimate.primaryLightIntensity.x,
                            Math.max(estimate.primaryLightIntensity.y, estimate.primaryLightIntensity.z)
                        )
                    );

                    this._light.specular.set(
                        estimate.primaryLightIntensity.x / intensity,
                        estimate.primaryLightIntensity.y / intensity,
                        estimate.primaryLightIntensity.z / intensity
                    );
                    this._light.diffuse.set(
                        estimate.primaryLightIntensity.x / intensity,
                        estimate.primaryLightIntensity.y / intensity,
                        estimate.primaryLightIntensity.z / intensity
                    );

                    this._light.intensity = intensity;
                }
            }
        }
    }

    protected placeInitialMarker(frame: XRFrame) {
        if (this._state == eXrState.searchingForHit || this._state == eXrState.initialPlacement) {
            const hit = this._initialHitTester!.getHitResult(frame);
            if (!hit) return;

            if (this._state == eXrState.searchingForHit) {
                if (!this._marker) {
                    this._marker = new XrMarker(this.v, this.scene, this._naturalScale);
                    this._marker.setEnabled(true);
                }
                this._state = eXrState.initialPlacement; //we got our first hit so we change state
            }

            if (this._state == eXrState.initialPlacement) {
                const quat = new Quaternion();
                hit.matrix.decompose(this._marker.scaling, quat, this._marker.position);
                this._marker.rotation = quat.toEulerAngles();
                // console.log(JSON.stringify({ markerPosition: this._marker.position }));
            }
        }
    }

    protected onSelectStart = (event: XRInputSourceEvent) => {
        if (this._state == eXrState.initialPlacement) {
            this.placeModel();
            this._state = eXrState.placed;

            this._initialHitTester!.dispose();
            this._initialHitTester = undefined;

            this._transientHitTester = new XrTransientHitTester(this._sessionMgr);
            this._transientHitTester.initialize();
            this._wasJustPlaced = true;
        } else if (this._state == eXrState.placed) {
            const fingers = this._transientHitTester?.getFingers(this._lastFrame!);

            if (fingers?.length === 1) {
                /** for one finger we do a hit test on kb3d to see if they pressed the model */
                this._selectStartScreenPos = this.screenNdcToPixels(event.inputSource.gamepad!.axes); //get the screen coordinates of the finger press
                const pickInfo = this.pick(this._selectStartScreenPos);
                if (pickInfo) {
                    // console.log({ pickInfo });
                    this._isTranslating = true;
                    this._dragNodeStartPos = this._rootNode.position.clone();
                    // this._dragNodeStartScreenPos = toScreen(this._rootNode.absolutePosition, this.getRigCamera());
                    // this._fingerStartScreenPos = this.screenNdcToPixels(fingers[0].inputSource.gamepad!.axes);
                    const dragHitTester = new XrTransientHitTester(this._sessionMgr);

                    const offset = this._dragNodeStartPos.subtract(pickInfo.pickedPoint.toVec3());
                    // console.log({nodePos: this._dragNodeStartPos, pickedPoint: pickInfo.pickedPoint});
                    const offsetRay = new XRRay(offset);

                    dragHitTester.initialize(offsetRay).then(() => {
                        this._dragHitTester = dragHitTester;
                    });
                } else {
                    this._isRotating = true;
                    this._goalYaw = this._rootNode.rotation.y;
                    this._lastScalar = event.inputSource.gamepad!.axes[0];
                }

                //simulate pointer down observable
                const args = this.v.interaction.getKb3dPointerArgs({
                    type: PointerEventTypes.POINTERDOWN,
                    event: new PointerEvent('pointerdown', { pointerId: event.inputSource.gamepad!.id } as any),
                    pickInfo: pickInfo!,
                });
                this.v.interaction.handlePointerDown(args);
                this._marker.show();
            } else if (fingers?.length === 2 && this.v.sceneNode.xrAllowScaling) {
                this._isScaling = true;
                this._lastScalar = this.getFingerSeparation(fingers) / this._rootNode.scaling.x;
                this._marker.show();
            }
        }
    };

    protected onSelectEnd = (event: XRInputSourceEvent) => {
        this._isTranslating = false;
        this._isRotating = false;
        this._isScaling = false;
        if (this._dragHitTester) {
            this._dragHitTester.dispose();
        }
        this._dragHitTester = undefined;
        this._fingerStartPos = undefined;

        //simulate pointer up observable
        const fingers = this._transientHitTester?.getFingers(this._lastFrame!);
        const screenCoord = this.screenNdcToPixels(event.inputSource.gamepad!.axes); //get the screen coordinates of the finger press
        const pickInfo = this.pick(screenCoord);
        const args = this.v.interaction.getKb3dPointerArgs({
            type: PointerEventTypes.POINTERUP,
            event: new PointerEvent('pointerup', { pointerId: event.inputSource.gamepad!.id } as any),
            pickInfo: pickInfo!,
        });
        this.v.interaction.handlePointerUp(args);

        //simulate the pointer tap observable if the input hasn't moved much
        if (this._state == eXrState.placed) {
            if (fingers?.length === 1 && !this._wasJustPlaced) {
                const deltaScreen = Vector2.Distance(this._selectStartScreenPos || screenCoord, screenCoord);
                if (deltaScreen < 5) {
                    const args = this.v.interaction.getKb3dPointerArgs({
                        type: PointerEventTypes.POINTERTAP,
                        event: new PointerEvent('pointertap', { pointerId: event.inputSource.gamepad!.id } as any),
                        pickInfo: pickInfo!,
                    });
                    this.v.interaction.handleClick(args);
                }
            }

            this._marker.hide();
            this._wasJustPlaced = false;
        }
    };

    protected processInput(frame: XRFrame) {
        if (this._state == eXrState.placed && (this._isTranslating || this._isRotating || this._isScaling)) {
            const fingers = this._transientHitTester!.getFingers(frame);

            if (this._isScaling) {
                if (fingers.length < 2) {
                    //the user removed their 2nd finger, so the scale is cancelled
                    this._isScaling = false;
                } else {
                    const separation = this.getFingerSeparation(fingers);
                    const scale = separation / this._lastScalar;
                    this._goalScale =
                        scale < this._scaleSnapHigh && scale > this._scaleSnapLow ? this._naturalScale : scale;
                }
            } else if (fingers.length === 2 && this.v.sceneNode.xrAllowScaling) {
                // happens when translating or rotating and another finger was added.  We switch to scaling in this instance
                this._isTranslating = false;
                this._isRotating = false;
                this._isScaling = true;
                const scale = this._rootNode.scaling.x;
                this._lastScalar = this.getFingerSeparation(fingers) / scale;
            } else if (this._isRotating) {
                const inputSource = fingers[0].inputSource;
                const dragX = inputSource.gamepad!.axes[0];
                this._goalYaw += (dragX - this._lastScalar) * ROTATION_RATE;
                this._lastScalar = dragX;
            } else if (this._isTranslating) {
                if (fingers.length > 0 && this._dragHitTester) {
                    const dragFingers = this._dragHitTester.getFingers(frame);
                    if (dragFingers.length && dragFingers[0].results.length) {
                        const dragFinger = dragFingers[0];

                        const dragFingerResult = dragFinger.results[0];
                        if (!this._fingerStartPos) this._fingerStartPos = dragFingerResult.position;

                        const deltaPos = dragFingerResult.position.subtract(this._fingerStartPos);
                        this._goalPosition = this._dragNodeStartPos!.add(deltaPos);
                    }
                }
            }
        }
    }

    protected placeModel() {
        this._goalPosition = this._marker.position.clone();
        this._goalYaw = this._marker.rotation.y;
        this._goalScale = this._naturalScale;
        this._scaleSnapHigh = this._naturalScale * SCALE_SNAP;
        this._scaleSnapLow = this._naturalScale / SCALE_SNAP;

        //we now add the marker to the root node so it can scale and move with the model
        this._marker.place(this._rootNode);

        if (this.v.sceneNode.xrAddShadow) {
            this._shadow = new XrShadowPlane(this.v, this.scene);
            this._shadow.place(this._rootNode);

            this._light = new XrLight(this.v, this.scene);

            if (this.v.sceneNode.xrLightingEstimation && this._session.requestLightProbe) {
                this._session.requestLightProbe().then(lp => (this._lightProbe = lp));
            }
        }

        this._rootNode.setEnabled(true);

        //this._marker.setEnabled(false);
        this._state = eXrState.placed;
    }

    /** does a pick of the kb3d scene (NOT a webxr hit test).  Accepts x/y as screen pixel coordinates */
    protected pick(screenCoord: Vector2) {
        /**
         * The WebXRCamera in babylon appears to be for reference only, and it's position/rotation
         * are updated on each frame to be the same as the viewer pose from XR.
         *
         * However, the forward ray from the WebXRCamera is not what we would expect, it's pointing
         * in exactly the opposite direction.  However, inside the WebXRCamera are rig cameras (only 1
         * in the case of AR) who are responsible for actually doing the babylon rendering.  The rig
         * camera direction is what we would expect.  For now we are using the rig camera to do
         * the picking, but we should keep an eye on this part as new babylon/webxr releases come out,
         *  as something seems amiss.
         */
        const hardwareScale = this.scene.getEngine().getHardwareScalingLevel();
        /** adjust by hardware scale as webxr does not support it, but the pick routine uses it */
        const pickInfo = this.v.interaction.pick(
            screenCoord.x * hardwareScale,
            screenCoord.y * hardwareScale,
            this.getRigCamera()
        );

        return pickInfo;
    }

    protected getFingerSeparation(fingers: IXrTransientHitResult[]): number {
        const firstFinger = fingers[0].inputSource.gamepad!.axes;
        const secondFinger = fingers[1].inputSource.gamepad!.axes;
        const deltaX = secondFinger[0] - firstFinger[0];
        const deltaY = secondFinger[1] - firstFinger[1];
        return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    }

    /** set's the transforms of the scene to the goal values */
    protected setSceneTransform() {
        if (this._state == eXrState.placed) {
            let invalidateRootBoundingBox = false;
            if (this._rootNode.scaling.x != this._goalScale) {
                this._rootNode.scaling = new Vector3(this._goalScale, this._goalScale, this._goalScale);
                invalidateRootBoundingBox = true;
            }
            if (this._rootNode.rotation.y != this._goalYaw) {
                this._rootNode.rotation.y = this._goalYaw; // = Quaternion.FromEulerAngles(0, this._goalYaw, 0);
                // this._marker.rotation.y = this._goalYaw;
                invalidateRootBoundingBox = true;
            }

            if (
                this._goalPosition &&
                !this._goalPosition.equalsToFloats(
                    this._rootNode.position.x,
                    this._rootNode.position.y,
                    this._rootNode.position.z
                )
            ) {
                this._rootNode.position.set(this._goalPosition.x, this._goalPosition.y, this._goalPosition.z);
                //this._marker.position.set(this._goalPosition.x, this._goalPosition.y, this._goalPosition.z);
                invalidateRootBoundingBox = true;
            }

            if (invalidateRootBoundingBox) {
                this.v.bbCache.invalidate(this.v.sceneNode);
            }
        }
    }

    protected getRigCamera() {
        const rigCamera = this._xrCamera.rigCameras[0];
        return rigCamera;
    }

    protected screenNdcToPixels(axes: readonly number[]) {
        const canvas = this.scene.getEngine().getRenderingCanvas() as HTMLCanvasElement;
        //need to convert NDC units to pixels
        const x = (axes[0] + 1) * (canvas.width / 2);
        const y = (axes[1] + 1) * (canvas.height / 2);

        return new Vector2(x, y);
    }

    protected screenPixelsToNdc(pos: { x: number; y: number }) {
        const canvas = this.scene.getEngine().getRenderingCanvas() as HTMLCanvasElement;
        return {
            x: pos.x / (canvas.width / 2) - 1,
            y: pos.y / (canvas.height / 2) - 2,
        };
    }
}
